import { Injectable, Injector } from '@angular/core';
import { removeFromArray } from '@reflact/tsutil';
import { InactiveBotServiceService } from '@shared-angular/services/inactive-bot-service.service';
import { RagLoggerService } from '@shared-angular/services/rag-logger/rag-logger.service';
import { eventsInActiveHookSchema, eventsOffSchema, eventsOnSchema, eventsTriggerSchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { getParamsForEventHook } from '../iframe-communication/EventHoolParamsHelper';
import type { KeyValService } from '../key-val.service';

type EventHookType = (eventName: string, ...args: unknown[]) => void;
type RegisteredHookType = { id: string, fn: EventHookType, hook: string };

/**
 * Zentrales Event-System des Bots (`RAGAI.EVENTS`): Registrieren, Auslösen und Entfernen von Hooks.
 * @remarks
 * Event-Namen folgen der Konvention `RAGAI.<MODUL>.<Aktion>` (z. B. `RAGAI.MSG.prompt`, `RAGAI.KEYVAL.storeKeyValEntry`).
 * Jedes RAGAI-Modul triggert bei praktisch jeder Aktion ein eigenes Event - das macht `RAGAI.EVENTS` zum
 * zentralen Beobachtungspunkt für alles, was im Bot passiert, unabhängig vom auslösenden Modul.
 * Der Hook-Name `"*"` (Wildcard) lauscht auf **alle** Events, nicht nur ein bestimmtes - wird u. a. intern vom
 * Debug-Manager genutzt, um jedes RAGAI-Event ins Debug-Log zu spiegeln.
 * `on`/`off`/`trigger` sind kurze Aliase für `listenTo`/`removeEventHook`/`triggerEventHook`.
 */
@Injectable({ providedIn: 'root' })
export class EventManagerService {
  public registeredHooks: RegisteredHookType[] = [];
  public _disabled: Record<string, boolean> = {};
  constructor(
    private logger: RagLoggerService,
    public inActiveService: InactiveBotServiceService,
    private injector: Injector
  ) { }

  private _keyValService: KeyValService | null = null;
  private get keyValService(): KeyValService {
    if (!this._keyValService) {
      const { KeyValService } = require('../key-val.service');
      this._keyValService = this.injector.get(KeyValService) as KeyValService;
    }
    return this._keyValService;
  }

  /** Registriert einen Listener für ein Event und gibt dessen ID zurück (zum späteren Entfernen). */
  public listenTo(hook: string, fn: EventHookType, id: string = crypto.randomUUID()) {
    hook = hook.split("window.RAGAI.").join("RAGAI.");
    this.registeredHooks.push({ id, fn, hook });
    this._trigger_RAGAI_EVENT("RAGAI.EVENTS.LISTENTO", { hook, id });
    return id;
  }
  /** Liest ein benanntes Feld aus rohen Event-Nutzdaten aus - normalisiert die je nach Event-Typ unterschiedliche Datenstruktur. */
  public _datafromEvent(eventName: string, eventData: any, field: string): any {
    if (eventData == null) return undefined;

    // Normalize eventName
    eventName = eventName.split("window.RAGAI.").join("RAGAI.");

    switch (eventName) {
      // ── MSG ──────────────────────────────────────────────────
      case 'RAGAI.MSG.prompt':
      case 'RAGAI.MSG.userMessage':
      case 'RAGAI.MSG.botMessage':
      case 'RAGAI.MSG.botBypass':
      case 'RAGAI.MSG.userBypass':
        if (field === 'text') return eventData.text ?? eventData.pInput ?? undefined;
        break;

      case 'RAGAI.MSG.botBypassComplete':
      case 'RAGAI.MSG.userBypassComplete':
        if (field === 'text') {
          // eventData.text kann ein HistoryItemResponse sein
          const val = eventData.text;
          if (val && typeof val === 'object' && val.content) {
            const textContent = val.content.find((c: any) => c.type === 'text');
            return textContent?.text ?? val;
          }
          return val;
        }
        break;

      case 'RAGAI.MSG.runComplete':
        // eventData ist ein HistoryItemResponse
        if (field === 'text') {
          if (eventData.content) {
            const textContent = eventData.content.find((c: any) => c.type === 'text');
            return textContent?.text ?? undefined;
          }
          return eventData.text ?? undefined;
        }
        if (field === 'input') return eventData.promptInput ?? eventData.input ?? undefined;
        break;

      case 'RAGAI.MSG.overwriteSystemPrompt':
        if (field === 'prompt') return eventData.prompt ?? undefined;
        if (field === 'text') return eventData.prompt ?? undefined;
        break;

      case 'RAGAI.MSG.overwriteWebsearchEnabled':
        if (field === 'input') return eventData.enabled ?? eventData.prompt ?? undefined;
        break;

      case 'RAGAI.MSG.pasteToPromptInput':
        if (field === 'text') return eventData.text ?? eventData.pInput ?? undefined;
        if (field === 'input') return eventData.text ?? eventData.pInput ?? eventData.input ?? undefined;
        break;

      // ── STAGE ────────────────────────────────────────────────
      case 'RAGAI.STAGE.ENTER':
      case 'RAGAI.STAGE.EXIT':
        // eventData ist ein StageDefinition-Objekt
        if (field === 'name') return eventData.name ?? undefined;
        if (field === 'title') return eventData.title ?? undefined;
        if (field === '_id') return eventData._id ?? undefined;
        if (field === 'hidden') return eventData.hidden ?? false;
        if (field === 'skills') return eventData.skills ?? [];
        if (field === 'index') return eventData.sortFloat ?? undefined;
        break;

      case 'RAGAI.STAGE.CHANGED':
        if (field === 'from') return eventData.from ?? undefined;
        if (field === 'to') return eventData.to ?? undefined;
        if (field === 'name') return eventData.to?.name ?? undefined;
        if (field === 'title') return eventData.to?.title ?? undefined;
        break;

      // ── KEYVAL / PROJECTKEYVAL ──────────────────────────────
      case 'RAGAI.KEYVAL.storeKeyValEntry':
      case 'RAGAI.KEYVAL.deleteKeyValEntry':
      case 'RAGAI.KEYVAL.getKeyValEntry':
      case 'RAGAI.PROJECTKEYVAL.storeKeyValEntry':
      case 'RAGAI.PROJECTKEYVAL.deleteKeyValEntry':
      case 'RAGAI.PROJECTKEYVAL.getKeyValEntry':
      case 'RAGAI.PROJECTKEYVAL.remoteUpdate':
      case 'RAGAI.PROJECTKEYVAL.remoteDelete':
        if (field === 'key') return eventData.key ?? undefined;
        if (field === 'value') return eventData.value ?? undefined;
        if (field === 'type') return eventData.type ?? undefined;
        break;

      case 'RAGAI.KEYVAL.getKeyValByType':
      case 'RAGAI.PROJECTKEYVAL.getKeyValByType':
        // eventData ist direkt das Ergebnis-Array
        return eventData;

      // ── WEBHOOK ──────────────────────────────────────────────
      case 'RAGAI.WEBHOOK.CALL.request':
        if (field === 'skillId') return eventData.skillId ?? undefined;
        if (field === 'request') return eventData.request ?? undefined;
        break;

      case 'RAGAI.WEBHOOK.CALL.result':
        if (field === 'skillId') return eventData.skillId ?? undefined;
        if (field === 'request') return eventData.request ?? undefined;
        if (field === 'result') return eventData.result ?? undefined;
        break;

      // ── OVERLAY ──────────────────────────────────────────────
      case 'RAGAI.OVERLAY.OPEN.text':
      case 'RAGAI.OVERLAY.OPEN.loading':
        if (field === 'text') return eventData.text ?? undefined;
        if (field === 'overlay') return eventData;
        break;

      case 'RAGAI.OVERLAY.OPEN.html':
        if (field === 'html') return eventData.html ?? undefined;
        if (field === 'overlay') return eventData;
        break;

      case 'RAGAI.OVERLAY.OPEN.textAndButtons':
        if (field === 'text') return eventData.text ?? undefined;
        if (field === 'buttons') return eventData.buttons ?? undefined;
        if (field === 'overlay') return eventData;
        break;

      // ── YOUTUBE ──────────────────────────────────────────────
      case 'RAGAI.YOUTUBE.SEEKTO':
        if (field === 'currentPlayPosition') return eventData.seconds ?? undefined;
        if (field === 'player') return eventData.player ?? undefined;
        break;

      case 'RAGAI.YOUTUBE.playerready':
        if (field === 'player') return eventData.player ?? undefined;
        if (field === 'currentState') return eventData.currentState ?? undefined;
        if (field === 'currentPlayPosition') return eventData.currentPlayPosition ?? undefined;
        break;

      case 'RAGAI.YOUTUBE.PLAY':
      case 'RAGAI.YOUTUBE.PAUSE':
      case 'RAGAI.YOUTUBE.STOP':
      case 'RAGAI.YOUTUBE.CHANGEVIDEO':
        if (field === 'player') return eventData.player ?? undefined;
        if (field === 'currentPlayPosition') return eventData.currentPlayPosition ?? eventData.seconds ?? undefined;
        break;

      // ── VIMEO ────────────────────────────────────────────────
      case 'RAGAI.VIMEO.playpositionchanged':
        if (field === 'currentPlayPosition') return eventData.seconds ?? eventData.currentPlayPosition ?? eventData;
        if (field === 'player') return eventData.player ?? undefined;
        break;

      case 'RAGAI.VIMEO.playerready':
      case 'RAGAI.VIMEO.PLAY':
      case 'RAGAI.VIMEO.PAUSE':
      case 'RAGAI.VIMEO.LOADVIDEO':
        if (field === 'player') return eventData.player ?? eventData;
        break;

      // ── AUDIO / SPEECH ───────────────────────────────────────
      case 'RAGAI.AUDIO.SPEECH.micconnected':
      case 'RAGAI.AUDIO.SPEECH.recording':
        if (field === 'stream') return eventData.stream ?? undefined;
        if (field === 'state') return eventData.state ?? undefined;
        break;

      case 'RAGAI.AUDIO.SPEECH.attRecive':
        if (field === 'text') return eventData.text ?? undefined;
        break;

      case 'RAGAI.AUDIO.SPEECH.repeatlastmessage':
        if (field === 'message') return eventData.message ?? undefined;
        if (field === 'text') return eventData.message ?? undefined;
        break;

      // ── BUTTON ───────────────────────────────────────────────
      case 'RAGAI.BUTTON.CLICK':
        if (field === 'text') return eventData.text ?? eventData.label ?? undefined;
        if (field === 'eventData') return eventData;
        break;

      // ── KI-TEXT ──────────────────────────────────────────────
      case 'RAGAI.AITEXT_OPENAI.CHATCOMPLETION.request':
        if (field === 'input') return eventData.request?.promptInput ?? undefined;
        if (field === 'result') return undefined;
        break;

      case 'RAGAI.AITEXT_OPENAI.CHATCOMPLETION.result':
        if (field === 'result') return eventData.result ?? undefined;
        if (field === 'input') return eventData.request?.promptInput ?? undefined;
        break;

      // ── KI-BILD ──────────────────────────────────────────────
      case 'RAGAI.AIIMAGE_OPENAI.CREATE.done':
        if (field === 'result') return eventData.result ?? eventData;
        if (field === 'prompt') return eventData.prompt ?? undefined;
        break;

      case 'RAGAI.AIIMAGE_OPENAI.ANALYZEIMAGE.done':
        if (field === 'result') return eventData.result ?? eventData;
        break;

      case 'RAGAI.AIIMAGE_FALAI.CREATE.done':
        if (field === 'result') return eventData.result ?? eventData;
        if (field === 'prompt') return eventData.prompt ?? undefined;
        break;

      // ── MAIL ─────────────────────────────────────────────────
      case 'RAGAI.MAIL.WEBHOOK.send':
      case 'RAGAI.MAIL.WEBHOOK.done':
        if (field === 'to') return eventData.to ?? eventData.envelope?.to ?? undefined;
        if (field === 'subject') return eventData.subject ?? eventData.envelope?.subject ?? undefined;
        if (field === 'body') return eventData.body ?? eventData.envelope?.body ?? undefined;
        break;

      // ── SCORM ────────────────────────────────────────────────
      case 'RAGAI.SCORM.INITED':
        if (field === 'key') return eventData.key ?? undefined;
        if (field === 'value') return eventData.value ?? undefined;
        break;

      // ── RISE / LXT ───────────────────────────────────────────
      case 'RAGAI.RISE.click':
        if (field === 'target') return eventData.target ?? undefined;
        if (field === 'RISE') return eventData.RISE ?? undefined;
        break;

      case 'RAGAI.RISE.ANSWER':
      case 'RAGAI.RISE.ANSWER.correct':
      case 'RAGAI.RISE.ANSWER.incorrect':
        if (field === 'target') return eventData.target ?? undefined;
        if (field === 'RISE') return eventData.RISE ?? eventData;
        break;

      case 'RAGAI.RISE.init':
      case 'RAGAI.RISE.pagechange':
        if (field === 'RISE') return eventData.RISE ?? eventData;
        break;

      case 'RAGAI.LXT.click':
        if (field === 'target') return eventData.target ?? undefined;
        if (field === 'LXT') return eventData.LXT ?? undefined;
        break;

      case 'RAGAI.LXT.ANSWER':
      case 'RAGAI.LXT.ANSWER.correct':
      case 'RAGAI.LXT.ANSWER.incorrect':
        if (field === 'target') return eventData.target ?? undefined;
        if (field === 'LXT') return eventData.LXT ?? eventData;
        break;

      case 'RAGAI.LXT.init':
      case 'RAGAI.LXT.pagechange':
        if (field === 'LXT') return eventData.LXT ?? eventData;
        break;

      // ── ADOBE CONNECT ────────────────────────────────────────
      case 'RAGAI.ADOBECONNECT.userjoined':
        if (field === 'user') return eventData.user ?? eventData;
        break;

      case 'RAGAI.ADOBECONNECT.userleft':
        if (field === 'userId') return eventData.userId ?? undefined;
        break;

      case 'RAGAI.ADOBECONNECT.userstatuschanged':
        if (field === 'status') return eventData.status ?? undefined;
        if (field === 'userId') return eventData.userId ?? undefined;
        break;

      case 'RAGAI.ADOBECONNECT.rolechanged':
        if (field === 'role') return eventData.role ?? undefined;
        break;

      case 'RAGAI.ADOBECONNECT.playstatechanged':
        if (field === 'playState') return eventData.playState ?? undefined;
        break;

      case 'RAGAI.ADOBECONNECT.podtitlechanged':
        if (field === 'podTitle') return eventData.podTitle ?? undefined;
        break;

      case 'RAGAI.ADOBECONNECT.sizechanged':
        if (field === 'width') return eventData.width ?? undefined;
        if (field === 'height') return eventData.height ?? undefined;
        break;

      case 'RAGAI.ADOBECONNECT.syncmessagereceived':
        if (field === 'msgVal') return eventData.msgVal ?? undefined;
        break;

      // ── EVENT MANAGER ────────────────────────────────────────
      case 'RAGAI.EVENTS.LISTENTO':
        if (field === 'hook') return eventData.hook ?? undefined;
        if (field === 'id') return eventData.id ?? undefined;
        break;
    }

    // ── AUDIO generisch (state-basierte Events) ─────────────
    if (eventName.startsWith('RAGAI.AUDIO.SPEECH.')) {
      if (field === 'state') return eventData.state ?? eventName.split('.').pop() ?? undefined;
      if (field === 'text') return eventData.text ?? undefined;
      if (field === 'stream') return eventData.stream ?? undefined;
      if (field === 'message') return eventData.message ?? undefined;
    }

    // ── Fallback: Direkter Property-Zugriff ────────────────
    if (field in eventData) {
      return eventData[field];
    }

    // Nested path support (z.B. "from.title")
    if (field.includes('.')) {
      const parts = field.split('.');
      let current = eventData;
      for (const part of parts) {
        if (current == null) return undefined;
        current = current[part];
      }
      return current;
    }

    return undefined;
  }
  /** Kurzalias für listenTo(). Registriert einen Listener für ein Event. */
  public on(hook: string | z.input<typeof eventsOnSchema>, fn?: EventHookType, id: string = crypto.randomUUID()) {
    const parsed = eventsOnSchema.safeParse(hook);
    if (parsed.success && typeof hook === 'object') {
      fn = parsed.data.fn as EventHookType ?? fn;
      id = parsed.data.id ?? id;
      hook = parsed.data.hook;
    }
    return this.listenTo(hook as string, fn!, id);
  }

  /** Kurzalias für removeEventHook(). Entfernt einen registrierten Listener anhand seiner ID. */
  public off(id: string | z.input<typeof eventsOffSchema>): void {
    const parsed = eventsOffSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') {
      id = parsed.data.id;
    }
    this.removeEventHook(id as string);
  }


  /** Entfernt einen registrierten Listener anhand seiner ID. */
  public removeEventHook(id: string) {
    removeFromArray(this.registeredHooks, (e => e.id == id));
  }

  /** Registriert einen Listener direkt (ohne Zod-Validierung), Standard-Hook ist die Wildcard `"*"` (alle Events). */
  public addEventHook(id: string, fn: EventHookType, hook: string = "*") {
    this.registeredHooks.push({ id, fn, hook });
  }
  /*
    public addEventHookStateUpdate(id: string, fn: EventHookType) {
      this.registeredHooks.push({ id, fn, hook: "io_in_stateUpdate" });
    }

    public addEventHookRunUpdate(id: string, fn: EventHookType) {
      this.registeredHooks.push({ id, fn, hook: "io_in_runUpdate" });
    }

    public addEventHookSkillUsed(id: string, fn: EventHookType) {
      this.registeredHooks.push({ id, fn, hook: "io_in_skillUsed" });
    } */
  /*
    public addEventHookRunComplete(id: string, fn: EventHookType) {
      this.registeredHooks.push({ id, fn, hook: "runComplete" });
    }
    */

  /** Registriert einen Listener speziell für das interne `io_in_addUserMessage`-Event. */
  public addEventHookAddUserMessage(id: string, fn: EventHookType) {
    this.registeredHooks.push({ id, fn, hook: "io_in_addUserMessage" });
  }

  /** Löst alle registrierten Listener aus, deren Hook exakt passt oder (bei Wildcard-Hooks) als Präfix matcht. */
  public triggerEventHook(hook: string, params: unknown) {
    hook = hook.split("window.RAGAI.").join("RAGAI.");

    this.logger.measureHook(hook, params);

    this.registeredHooks.forEach((rh) => {
      try {
        if (hook == rh.hook) {
          rh.fn(hook, getParamsForEventHook(hook, params));
          return;
        }
        // Nur bei registriertem Wildcard-Hook ("*") als Präfix werten, sonst matchen z.B. "X.ANSWER" auch "X.ANSWER.correct"
        if (rh.hook.includes("*") && hook.startsWith(rh.hook.split("*")[0] ?? "")) {
          rh.fn(hook, getParamsForEventHook(hook, params));
        }
      } catch (e) {
        console.warn(`Error on exec Eventhook with id ${rh.id} check you js`, e);
      }
    });
  }

  /** Liefert einen Hook, der auslöst, sobald der Nutzer die angegebene Anzahl Sekunden inaktiv war. */
  public inActiveHook(seconds: number | z.input<typeof eventsInActiveHookSchema>) {
    const parsed = eventsInActiveHookSchema.safeParse(seconds);
    if (parsed.success && typeof seconds === 'object') {
      seconds = parsed.data.seconds;
    }
    return this.inActiveService.get(seconds as number);
  }



  private _callerInfo(instance: unknown): string {
    if (!instance) return "";

    const stack = new Error().stack?.split('\n') ?? [];
    //console.log("Stack:", stack);
    // Gehe von hinten durch den Stack (also vom Ursprung des Aufrufs aus)

    for (let i = stack.length - 1; i >= 0; i--) {
      // @ts-ignore
      const line = stack[i].trim();
      if (line.includes("<anonymous>")) {
        let found = stack[i - 1];
        found = found?.trimStart();
        found = found?.substring(3, found.length - 1);
        found = found?.trimStart();
        found = found?.split(" ")[0];
        found = found?.trim();
        return found ?? "";
      }
    }
    return "";
  }

  /** Kurzalias für triggerEventHook(). Löst ein benanntes Event manuell aus. */
  public trigger(name: string | z.input<typeof eventsTriggerSchema>, params: any = {}): void {
    const parsed = eventsTriggerSchema.safeParse(name);
    if (parsed.success && typeof name === 'object') {
      params = parsed.data.params ?? params;
      name = parsed.data.name;
    }
    this.triggerEventHook(name as string, params);
  }

  /** Interner Helfer: leitet aus dem Aufruf-Stack den auslösenden RAGAI-Modulnamen ab und triggert das passende `RAGAI.<MODUL>.<METHODE>`-Event. */
  public _trigger_RAGAI_EVENT(instance: unknown | string, eventParams: any = {}, additionalEventInfo: string = ""): string {
    let returnStr = "";
    if (typeof instance === "string") {
      let eventName = instance;
      if (additionalEventInfo !== "") {
        eventName += `.${additionalEventInfo}`;
      }
      this.triggerEventHook(eventName, eventParams);
      returnStr = eventName;
      return returnStr;
    }
    const managerCallName = this._callerInfo(this);

    const serviceName = managerCallName.split(".")[0];
    const method = managerCallName.split(".")[1];
    //@ts-ignore
    Object.entries(window.RAGAI).forEach(([key, value]) => {
      //@ts-ignore
      const cName: string = window.RAGAI[key].constructor.name;
      if (
        serviceName === cName &&
        cName.startsWith("_") &&
        /^[A-Z0-9_]+$/.test(key)) {
        let eventName = "RAGAI." + key + "." + method?.toUpperCase();
        //        console.log(eventName);
        if (additionalEventInfo !== "") {
          eventName += `.${additionalEventInfo}`;
        }


        this.triggerEventHook(eventName, eventParams);
        returnStr = eventName;
        return eventName;

        //console.log("Value:", value);
      }
    });
    return returnStr;
  }

  /** Deaktiviert, aktiviert oder entfernt ein registriertes Event anhand seiner ID. */
  public async manage(action: 'disable' | 'enable' | 'remove', eventId: string): Promise<void> {
    if (action === 'disable') {
      this._disabled[eventId] = true;
      await this.keyValService.storeKeyValEntry("evt_disabled_" + eventId, true, "EVENT-MANAGER");
    } else if (action === 'enable') {
      delete this._disabled[eventId];
      await this.keyValService.deleteKeyValEntry("evt_disabled_" + eventId, "EVENT-MANAGER");
    } else if (action === 'remove') {
      this.off(eventId);
      delete this._disabled[eventId];
      await this.keyValService.deleteKeyValEntry("evt_disabled_" + eventId, "EVENT-MANAGER");
    }
  }
}

